import './upload-data-by-excel-list.scss'
import moment from 'moment'
import * as XLSX from 'xlsx'
import imgSrc from './spinner.gif'
import saleInvoiceService from '../sale-invoice/sale-invoice.service'
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FiUpload, FiFileText, FiX, FiCheckCircle, FiAlertCircle, FiLoader, FiTrash2 } from 'react-icons/fi';
import DropdownService from '../software-dynamic-version-6/api.service-v-6'
import { ToastContainer, toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";

// Security: XSS Sanitizer
const sanitize = (str) => {
  if (str == null) return '';
  return String(str).replace(/[<>]/g, '');
};

// Utility: Precise decimal handling
const numStr = (v, d = 2) => {
  if (v == null || v === "") return Number(0).toFixed(d);
  const num = Math.round((parseFloat(v) + Number.EPSILON) * Math.pow(10, d)) / Math.pow(10, d);
  return num.toFixed(d);
};

// Utility: Safe Excel date parsing
const parseExcelDate = (value) => {
  if (typeof value !== 'number' || value < 1 || value > 50000) return null;
  try {
    const epoch = new Date(1899, 11, 30);
    const result = new Date(epoch.getTime() + value * 86400000);
    return isNaN(result) ? null : result.toISOString().split('T')[0];
  } catch {
    return null;
  }
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12'
];

const UploadExcelDataList = () => {
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [Files, setFiles] = useState([]);
  const [dataOfFiles, setDataOfFiles] = useState(null);
  const [isMasterLoading, setIsMasterLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isUploading, setIsUploading] = useState(false);

  const [invoiceMaster, setInvoiceMaster] = useState({
    sellerData: null,
    hsCodeMap: {},
    buyerMap: {}
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    async function loadMasters() {
      try {
        setIsMasterLoading(true);
        const companyRes = await saleInvoiceService.getCompanyView();
        
        if (!companyRes?.data?.recordset?.[0]) {
          throw new Error('Company data not found');
        }
        
        const company = companyRes.data.recordset[0];
        const [hsRes, buyerRes] = await Promise.all([
          saleInvoiceService.getItemDescCode(company.CompanyID),
          DropdownService.getAllForList("buyer")
        ]);

        setInvoiceMaster({
          sellerData: {
            sellerBusinessName: company.CompanyName,
            sellerNTNCNIC: company.CompanyNTNCNIC,
            sellerProvince: company.CompanyProvinceName,
            sellerAddress: company.CompanyAddress
          },
          hsCodeMap: Object.fromEntries(
            (hsRes?.data || []).map(r => [r.hS_CODE, `${r.hS_CODE} | ${r.description}`])
          ),
          buyerMap: Object.fromEntries(
            (buyerRes?.data?.recordset || []).map(b => [b.buyerNTNCNIC, b.buyerAddress])
          )
        });
      } catch (error) {
        console.error("Master data loading failed:", error);
        showAlert({
          heading: 'Initialization Error',
          message: 'Failed to load master data. Please refresh the page.',
          type: 'error',
          autoClose: false
        });
      } finally {
        setIsMasterLoading(false);
      }
    }
    loadMasters();
  }, []);

  const hideLoader = useCallback(() => {
    const loader = document.getElementById('loader');
    const successMsg = document.getElementById('successMessage');
    if (loader) loader.style.display = 'none';
    if (successMsg) successMsg.style.display = 'none';
  }, []);

  const showLoader = useCallback(() => {
    const loader = document.getElementById('loader');
    if (loader) loader.style.display = 'block';
    const successMsg = document.getElementById('successMessage');
    if (successMsg) successMsg.style.display = 'none';
  }, []);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const resetFileInput = useCallback(() => {
    setFiles([]);
    setDataOfFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const validateFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    
    if (!ALLOWED_TYPES.includes(file.type)) {
      return { valid: false, error: 'Invalid file type. Please upload .xlsx or .xlsm files only.' };
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `File too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 10MB.` };
    }
    
    return { valid: true };
  };

  const inputChange = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const validation = validateFile(file);
    
    if (!validation.valid) {
      showAlert({
        heading: 'Invalid File',
        message: validation.error,
        type: 'error'
      });
      resetFileInput();
      return;
    }

    // Create abort controller for this operation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setDataOfFiles(file);
    
    const reader = new FileReader();
    
    reader.onabort = () => {
      console.log('File reading aborted');
    };
    
    reader.onerror = () => {
      showAlert({
        heading: 'Read Error',
        message: 'Failed to read file. Please try again.',
        type: 'error'
      });
      resetFileInput();
    };
    
    reader.onload = (event) => {
      if (abortControllerRef.current?.signal.aborted) return;
      
      try {
        setFiles([{ 
          data: file, 
          fileImage: event.target.result,
          id: Date.now() // Unique ID for key prop
        }]);
      } catch (error) {
        console.error("Error processing file:", error);
        showAlert({
          heading: 'Processing Error',
          message: 'Failed to process file data',
          type: 'error'
        });
        resetFileInput();
      }
    };

    reader.readAsArrayBuffer(file);
  }, [resetFileInput]);

  const formatFileSize = (sizeInBytes) => {
    if (!sizeInBytes || sizeInBytes === 0) return '0 B';
    if (sizeInBytes < 1024) return sizeInBytes + ' B';
    if (sizeInBytes < 1024 * 1024) return (sizeInBytes / 1024).toFixed(2) + ' KB';
    return (sizeInBytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const DeleteFile = useCallback(() => {
    if (window.confirm("Are you sure you want to remove this file?")) {
      resetFileInput();
    }
  }, [resetFileInput]);

  // Memoized expensive calculations
  const filterValidExcelDataStrict = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    const invalidEntryNos = new Set(
      data.filter(row => row.ValidationDetails !== "Valid")
          .map(row => row.EntryNo)
    );
    return data.filter(row => !invalidEntryNos.has(row.EntryNo));
  }, []);

  const filterNotValidExcelDataStrict = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    return data.filter(row => row.ValidationDetails !== "Valid");
  }, []);

  const groupByEntryNo = useCallback((data) => {
    if (!Array.isArray(data)) return {};
    return data.reduce((acc, row) => {
      if (!row?.EntryNo) return acc;
      if (!acc[row.EntryNo]) acc[row.EntryNo] = [];
      acc[row.EntryNo].push(row);
      return acc;
    }, {});
  }, []);

  const getHSCodeDetail = useCallback((hsCode) => {
    return invoiceMaster.hsCodeMap[hsCode] || "-";
  }, [invoiceMaster.hsCodeMap]);

  const getBuyerAddress = useCallback((buyerNTNCNIC) => {
    return invoiceMaster.buyerMap[buyerNTNCNIC] || "";
  }, [invoiceMaster.buyerMap]);

  const cleanExcelData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    
    return data.map(row => {
      const hasTotalValuesColumn = row && 
        Object.prototype.hasOwnProperty.call(row, "totalValues") &&
        row.totalValues !== "";

      return {
        ...row,
        quantity: numStr(row.quantity, 2),
        salesTaxApplicable: numStr(row.salesTaxApplicable, 2),
        totalValues: hasTotalValuesColumn ? numStr(row.totalValues, 2) : "0.00",
        valueSalesExcludingST: numStr(row.valueSalesExcludingST, 0),
        fixedNotifiedValueOrRetailPrice: numStr(row.fixedNotifiedValueOrRetailPrice, 0),
        extraTax: numStr(row.extraTax, 0),
        furtherTax: numStr(row.furtherTax, 0),
        salesTaxWithheldAtSource: numStr(row.salesTaxWithheldAtSource, 0),
        HSCode: String(row.HSCode || "").replace(":-", ""),
        buyerNTNCNIC: String(row.buyerNTNCNIC || ""),
      };
    });
  }, []);

  const buildExcelData = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length < 2) return [];
    const headers = rows[0];
    
    if (!Array.isArray(headers)) return [];

    return rows.slice(1).map((row, ind) => {
      const obj = { ExcelRowID: ind + 2 };
      
      headers.forEach((header, index) => {
        if (!header || !String(header).trim()) return;
        
        let value = row?.[index] ?? "";
        const headerStr = String(header).toLowerCase();

        // Safe date parsing
        if (headerStr.includes("date") && typeof value === "number") {
          const parsedDate = parseExcelDate(value);
          if (parsedDate) value = parsedDate;
        }

        // Percentage handling
        if (typeof value === "number" && headerStr.includes("rate")) {
          value = `${value * 100}%`;
        }

        obj[header.trim()] = value;
      });

      return obj;
    });
  }, []);

  const readExcelFile = useCallback((file, signal) => {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const reader = new FileReader();
      
      const cleanup = () => {
        reader.onload = null;
        reader.onerror = null;
        reader.onabort = null;
      };

      reader.onload = (event) => {
        try {
          if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
          }
          
          const data = event.target.result;
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
          
          if (!workbook.SheetNames?.length) {
            throw new Error('No sheets found in Excel file');
          }
          
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          const rows = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            raw: false
          });
          
          cleanup();
          resolve(rows);
        } catch (err) {
          cleanup();
          reject(err);
        }
      };

      reader.onerror = () => {
        cleanup();
        reject(new Error('Failed to read file'));
      };
      
      reader.onabort = () => {
        cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
      };

      if (signal) {
        signal.addEventListener('abort', () => {
          reader.abort();
        });
      }

      try {
        reader.readAsBinaryString(file);
      } catch (err) {
        cleanup();
        reject(err);
      }
    });
  }, []);

  const buildInvoicePayload = useCallback((entryRows, isSendBox = true) => {
    if (!entryRows?.length) return null;
    if (!invoiceMaster?.sellerData) {
      throw new Error("Invoice master data missing");
    }

    const first = entryRows[0];
    const seller = invoiceMaster.sellerData;

    const jsonItems = entryRows.map(row => ({
      hsCode: String(row.HSCode || "").replace(":-", ""),
      productDescription: String(row.productDescription || ""),
      rate: row.rate,
      uoM: row.UOM,
      quantity: numStr(row.quantity, 2),
      totalValues: numStr(row.totalValues, 2),
      valueSalesExcludingST: numStr(row.valueSalesExcludingST, 0),
      fixedNotifiedValueOrRetailPrice: numStr(row.fixedNotifiedValueOrRetailPrice, 0),
      salesTaxApplicable: numStr(row.salesTaxApplicable, 2),
      salesTaxWithheldAtSource: numStr(row.salesTaxWithheldAtSource, 0),
      extraTax: numStr(row.extraTax, 0),
      furtherTax: numStr(row.furtherTax, 0),
      sroScheduleNo: String(row.sroScheduleNo || ""),
      fedPayable: numStr(row.fedPayable, 0),
      discount: numStr(row.discount, 0),
      saleType: row.transactioN_Desc,
      sroItemSerialNo: String(row.sroItemSerialNo || "")
    }));

    const saleInvoiceJSON = {
      invoiceType: first.invoiceType || "Sale Invoice",
      invoiceDate: first.invoiceDate,
      sellerNTNCNIC: seller.sellerNTNCNIC,
      sellerBusinessName: seller.sellerBusinessName,
      sellerProvince: first.sellerProvince || seller.sellerProvince,
      sellerAddress: seller.sellerAddress,
      buyerNTNCNIC: first.buyerNTNCNIC,
      buyerBusinessName: first.buyerBusinessName,
      buyerProvince: first.buyerProvince,
      buyerAddress: getBuyerAddress(first.buyerNTNCNIC),
      buyerRegistrationType: first.buyerRegistrationType,
      invoiceRefNo: String(first.invoiceRefNo || ""),
      ChallanNo: String(first.ChallanNo || ""),
      ...(isSendBox && { scenarioId: String(first.scenarioId || "").toUpperCase() }),
      items: jsonItems
    };

    const mainData = {
      docDescription: first.invoiceType,
      invoiceDate: first.invoiceDate,
      sellerNTNCNIC: seller.sellerNTNCNIC,
      buyerNTNCNIC: first.buyerNTNCNIC,
      invoiceRefNo: String(first.invoiceRefNo || ""),
      scenarioId: isSendBox ? String(first.scenarioId || "") : "",
      FBRSaleInvoiceNo: null,
      ChallanNo: String(first.ChallanNo || ""),
      SaleInvoiceJSON: JSON.stringify(saleInvoiceJSON)
    };

    const details = entryRows.map(row => ({
      hS_CODE: String(row.HSCode || "").replace(":-", ""),
      HSCodeDetail: getHSCodeDetail(String(row.HSCode || "").replace(":-", "")),
      productDescription: String(row.productDescription || ""),
      rate: row.rate,
      uoM: row.UOM,
      quantity: numStr(row.quantity, 2),
      totalValues: numStr(row.totalValues, 2),
      valueSalesExcludingST: numStr(row.valueSalesExcludingST, 0),
      fixedNotifiedValueOrRetailPrice: numStr(row.fixedNotifiedValueOrRetailPrice, 0),
      salesTaxApplicable: numStr(row.salesTaxApplicable, 2),
      salesTaxWithheldAtSource: numStr(row.salesTaxWithheldAtSource, 0),
      extraTax: numStr(row.extraTax, 0),
      furtherTax: numStr(row.furtherTax, 0),
      sroScheduleNo: String(row.sroScheduleNo || ""),
      fedPayable: numStr(row.fedPayable, 0),
      discount: numStr(row.discount, 0),
      transactioN_Desc: row.transactioN_Desc,
      sroItemSerialNo: String(row.sroItemSerialNo || ""),
      FBRSaleInvoiceDetailNo: null
    }));

    return { ...mainData, details, EntryNo: first.EntryNo };
  }, [invoiceMaster, getHSCodeDetail, getBuyerAddress]);

  const prepareInvoicesFromExcel = useCallback((finalFilteredData) => {
    const grouped = groupByEntryNo(finalFilteredData);
    return Object.values(grouped)
      .map(entryRows => buildInvoicePayload(entryRows))
      .filter(Boolean);
  }, [groupByEntryNo, buildInvoicePayload]);

  // Concurrent insertion with error isolation
  const insertMultipleInvoices = useCallback(async (payloads, signal) => {
    if (!Array.isArray(payloads) || payloads.length === 0) {
      throw new Error("No invoice payloads provided");
    }

    const CONCURRENCY = 3;
    const results = { success: [], failed: [] };
    
    for (let i = 0; i < payloads.length; i += CONCURRENCY) {
      if (signal?.aborted) {
        throw new DOMException('Upload aborted', 'AbortError');
      }

      const batch = payloads.slice(i, i + CONCURRENCY);
      
      const batchPromises = batch.map(async (payload) => {
        try {
          if (!payload.details?.length) {
            throw new Error("Invoice has no detail rows");
          }
          const response = await saleInvoiceService.insert(payload);
          return { 
            status: 'success', 
            data: { 
              EntryNo: sanitize(payload.EntryNo) || "N/A", 
              SaleInvoiceID: response?.SaleInvoiceID 
            } 
          };
        } catch (err) {
          return { 
            status: 'error', 
            data: { 
              EntryNo: sanitize(payload.EntryNo) || "N/A", 
              error: sanitize(err.message) || "Unknown error"
            } 
          };
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      
      batchResults.forEach(r => {
        if (r.status === 'success') results.success.push(r.data);
        else results.failed.push(r.data);
      });
      
      setUploadProgress({ current: Math.min(i + CONCURRENCY, payloads.length), total: payloads.length });
    }
    
    return results;
  }, []);

  const handleInsertResult = useCallback((result) => {
    hideLoader();
    setIsUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    
    if (result.success.length > 0) {
      resetFileInput();
      showAlert({
        heading: 'Success',
        message: `${result.success.length} invoice(s) inserted successfully.`,
        type: 'success',
        autoClose: 5000,
      });
    }

    if (result.failed.length > 0) {
      showAlert({
        heading: result.success.length > 0 ? 'Partial Failure' : 'Upload Failed',
        message: `${result.failed.length} invoice(s) failed. Check console for details.`,
        type: result.success.length > 0 ? 'warning' : 'error',
        autoClose: 10000,
      });
      
      console.error("Failed uploads:", result.failed);
    }
  }, [hideLoader, resetFileInput]);

  const formSubmit = async (e) => {
    e.preventDefault();
    
    if (!dataOfFiles || isUploading) return;

    // Validation check
    const validation = validateFile(dataOfFiles);
    if (!validation.valid) {
      showAlert({ heading: 'Error', message: validation.error, type: 'error' });
      return;
    }

    // Create new abort controller for this submission
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setIsUploading(true);
      showLoader();
      
      const rows = await readExcelFile(dataOfFiles, signal);
      
      if (signal.aborted) return;
      
      if (!rows || rows.length < 2) {
        throw new Error('Excel file appears to be empty or invalid');
      }

      const excelData = buildExcelData(rows);
      
      if (signal.aborted) return;
      
      if (excelData.length === 0) {
        throw new Error('No data rows found in Excel file');
      }

      const cleanedData = cleanExcelData(excelData);
      const invalidRows = filterNotValidExcelDataStrict(cleanedData);
      
      if (invalidRows.length > 0) {
        // Show first 3 errors in toast, rest in console
        invalidRows.slice(0, 3).forEach(row => {
          showAlert({
            heading: "Validation Error",
            message: `Row ${row.ExcelRowID}: ${sanitize(row.ValidationDetails)}`,
            type: "error",
            autoClose: 10000,
          });
        });
        
        if (invalidRows.length > 3) {
          console.error("Additional validation errors:", invalidRows.slice(3));
        }
        
        const shouldProceed = window.confirm(
          `${invalidRows.length} rows have validation errors and will be excluded. Proceed with ${cleanedData.length - invalidRows.length} valid rows?`
        );
        
        if (!shouldProceed) {
          hideLoader();
          setIsUploading(false);
          return;
        }
      }

      const finalFilteredData = filterValidExcelDataStrict(cleanedData);
      
      if (finalFilteredData.length === 0) {
        throw new Error('No valid invoices found after filtering');
      }

      const invoicePayloads = prepareInvoicesFromExcel(finalFilteredData);
      
      if (invoicePayloads.length === 0) {
        throw new Error('Failed to prepare invoice data');
      }

      const insertResult = await insertMultipleInvoices(invoicePayloads, signal);
      
      if (signal.aborted) return;
      
      handleInsertResult(insertResult);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Upload cancelled by user');
        return;
      }
      
      console.error("Excel processing failed:", error);
      showAlert({
        heading: 'Processing Error',
        message: sanitize(error.message) || 'Failed to process Excel file',
        type: 'error',
        autoClose: 8000,
      });
      hideLoader();
      setIsUploading(false);
    }
  };

  const showAlert = ({ heading, message, type = 'info', autoClose = 3000, position = 'bottom-right' }) => {
    if (!heading || !message) {
      console.error('showAlert requires both heading and message.');
      return;
    }

    toast[type](
      <div>
        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px', color: '#000' }}>
          {sanitize(heading)}
        </div>
        <div style={{ fontSize: '14px', color: '#000' }}>
          {sanitize(message)}
        </div>
      </div>,
      {
        position,
        autoClose,
        style: {
          borderRadius: '6px',
          padding: '12px',
        },
      }
    );
  };

  // Progress bar component
  const ProgressBar = () => {
    if (!isUploading || uploadProgress.total === 0) return null;
    
    const percentage = Math.round((uploadProgress.current / uploadProgress.total) * 100);
    
    return (
      <div style={{ marginTop: '16px', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', color: '#6b7280' }}>
          <span>Uploading invoices...</span>
          <span>{uploadProgress.current} of {uploadProgress.total} ({percentage}%)</span>
        </div>
        <div style={{ width: '100%', height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
          <div 
            style={{ 
              width: `${percentage}%`, 
              height: '100%', 
              backgroundColor: '#4f46e5', 
              transition: 'width 0.3s ease',
              borderRadius: '3px'
            }} 
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <ToastContainer
        theme="light"
        toastStyle={{
          backgroundColor: '#ffffff',
          color: '#000000',
          border: '1px solid #ccc',
          borderRadius: '6px',
          padding: '12px',
        }}
      />
      <div className='content-wrapper upload-supplier-wrapper'>
        <div className="kb-modal-data-title">
          <div className="kb-data-title">
            <h6 className='upload-excel-heading'>Upload Excel File</h6>
          </div>
        </div>
        
        <form onSubmit={formSubmit}>
          <div className='form-upload-wrapper'>
            <div 
              className='upload-file-main-div' 
              id='upload-file-div'
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleDragLeave(e);
                const droppedFiles = e.dataTransfer.files;
                if (droppedFiles.length > 0) {
                  // Create synthetic event to reuse validation logic
                  const syntheticEvent = { target: { files: droppedFiles } };
                  inputChange(syntheticEvent);
                }
              }}
              style={{
                border: isDragOver ? '2px dashed #4f46e5' : '2px dashed #00335b',
                borderRadius: '12px',
                padding: '40px 20px',
                textAlign: 'center',
                backgroundColor: isDragOver ? '#eef2ff' : 'rgba(129, 148, 170, 0.1803921569)',
                transition: 'all 0.3s ease',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                position: 'relative',
                overflow: 'hidden',
                opacity: isUploading ? 0.6 : 1
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="excelFile"
                name="excelFile"
                multiple={false}
                accept=".xlsx,.xls,.xlsm"
                onChange={inputChange}
                disabled={isUploading}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  opacity: 0,
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  zIndex: 2
                }}
              />
              
              <div style={{ pointerEvents: 'none', zIndex: 1, position: 'relative' }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  margin: '0 auto 16px',
                  backgroundColor: isDragOver ? '#4f46e5' : '#00335b',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}>
                  {isUploading ? (
                    <FiLoader size={28} color="white" style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <FiUpload size={28} color="white" />
                  )}
                </div>
                
                <p style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '16px', 
                  fontWeight: 600,
                  color: '#1f2937'
                }}>
                  {isDragOver ? 'Drop your file here' : isUploading ? 'Processing...' : 'Drag and drop your Excel file'}
                </p>
                
                <p style={{ 
                  margin: 0, 
                  fontSize: '14px', 
                  color: '#6b7280' 
                }}>
                  or <span style={{ 
                    color: '#4f46e5', 
                    fontWeight: 600,
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}>browse to choose file</span>
                </p>
                
                <p style={{ 
                  marginTop: '12px', 
                  fontSize: '12px', 
                  color: '#9ca3af' 
                }}>
                  Supports: .xlsx, .xls, .xlsm (Max 10MB)
                </p>
              </div>
            </div>
          </div>

          <div className="kb-buttons-box" style={{ marginTop: '24px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button
              type="submit"
              className="btn btn-primary form-submit"
              disabled={isMasterLoading || Files.length === 0 || isUploading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '6px',
                border: 'none',
                backgroundColor: isMasterLoading || Files.length === 0 || isUploading ? '#9ca3af' : '#00335b',
                color: 'white',
                cursor: isMasterLoading || Files.length === 0 || isUploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {isMasterLoading ? (
                <>
                  <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                  Loading master data...
                </>
              ) : isUploading ? (
                <>
                  <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                  Processing...
                </>
              ) : (
                <>
                  <FiCheckCircle size={16} />
                  Upload Invoice
                </>
              )}
            </button>

            {isUploading && (
              <button
                type="button"
                onClick={() => {
                  if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                  }
                  setIsUploading(false);
                  hideLoader();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FiX size={16} />
                Cancel
              </button>
            )}

            <img src={imgSrc} id='loader' style={{ display: 'none', width: '24px', height: '24px' }} alt="loading" />
            <p id='successMessage' style={{ 
              display: 'none', 
              color: '#059669', 
              fontWeight: 600,
              margin: 0,
              alignItems: 'center',
              gap: '6px'
            }}>
              <FiCheckCircle /> Upload Successfully!
            </p>
          </div>

          <ProgressBar />
        </form>

        {Files.length > 0 && !isUploading && (
          <div className="kb-attach-box" style={{ marginTop: '32px' }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', marginBottom: '24px' }} />
            {Files.map((data) => {
              const { name, lastModifiedDate, size } = data.data;
              return (
                <div 
                  key={data.id} 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s',
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#dbeafe',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <FiFileText size={24} color="#2563eb" />
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h6 style={{ 
                      margin: '0 0 4px 0', 
                      fontSize: '14px', 
                      fontWeight: 600,
                      color: '#111827',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={name}>
                      {sanitize(name)}
                    </h6>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '13px', 
                      color: '#6b7280' 
                    }}>
                      <span style={{ fontWeight: 500 }}>{formatFileSize(size)}</span>
                      <span style={{ margin: '0 8px', color: '#d1d5db' }}>•</span>
                      <span>Modified: {moment(lastModifiedDate).format('DD/MM/YY')}</span>
                    </p>
                  </div>

                  <button 
                    onClick={DeleteFile}
                    disabled={isUploading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      flexShrink: 0,
                      opacity: isUploading ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isUploading) e.currentTarget.style.backgroundColor = '#fecaca';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fee2e2';
                    }}
                  >
                    <FiTrash2 size={16} />
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default UploadExcelDataList;